import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import Lf23Loader from "../../components/Lf23Loader";
import MappaTratte, { COLORE_STATO } from "../ReportPubblico/MappaTratte";
import { API_URL, authHeaders, nf, quartiere, formatData, Stella } from "./comuni";

/**
 * Mappa delle tratte da recuperare (task 548): la vista "Mappa" del tab, alternativa alla
 * tabella per comporre il carrello del giro di recupero.
 *
 * Ogni tratta e' una linea del colore della sua stella (rossa / gialla, livello deciso dal
 * backend). Toccandola si apre il dettaglio nel pannello accanto, con "Aggiungi al
 * recupero": il carrello e' quello della pagina (perno di TabRecuperi), quindi tabella,
 * mappa e contatore dicono sempre la stessa cosa. Una tratta nel carrello diventa blu e
 * tiene il colore della stella come bordo.
 *
 * Le geometrie arrivano da GET /recuperi/geometrie solo quando servono: pesano, e chi
 * resta sulla tabella non le scarica.
 */

/* Alone della tratta scelta: l'arancione di default di MappaTratte e' il giallo delle
   stelle, qui serve un colore che non sia ne' una stella ne' il blu del carrello. */
const COLORE_SCELTA = "#0F172A";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const nomeStella = (c) => (c.livello === "rossa" ? "Stella rossa" : "Stella gialla");
const mancate = (c) => `${c.volte} mancat${c.volte === 1 ? "a" : "e"} di fila`;

/**
 * Le geometrie delle tratte da recuperare, caricate quando `attivo` diventa vero.
 * Sono legate a `dati` (l'elenco): dopo un Aggiorna si richiedono, tenendo intanto
 * quelle vecchie, cosi' la mappa aperta non sparisce dietro a un loader.
 * Vive nel corpo del tab: passare da Tabella a Mappa e ritorno non le riscarica.
 */
export const useGeometrieRecuperi = (zona, dati, attivo) => {
    const [geo, setGeo] = useState({ per: null, geometrie: null, errore: null });
    const aggiornate = geo.per === dati;

    useEffect(() => {
        if (!attivo || !dati || aggiornate) return undefined;
        let annullato = false;
        axios.get(`${API_URL}/gestione-percorsi/recuperi/geometrie`, { headers: authHeaders(), params: { zona } })
            .then((res) => { if (!annullato) setGeo({ per: dati, geometrie: res.data?.geometrie || {}, errore: null }); })
            .catch((err) => {
                console.error("Errore geometrie tratte da recuperare:", err);
                if (!annullato) setGeo((g) => ({ ...g, per: dati, errore: "Impossibile caricare la mappa delle tratte da recuperare." }));
            });
        return () => { annullato = true; };
    }, [attivo, dati, zona, aggiornate]);

    const riprova = useCallback(() => setGeo((g) => ({ ...g, per: null, errore: null })), []);
    return {
        geometrie: geo.geometrie,
        loading: !!attivo && !aggiornate && !geo.geometrie,
        errore: aggiornate ? geo.errore : null,
        riprova,
    };
};

/* Le righe dell'elenco nella forma di MappaTratte. In ordine di CODICE e non dell'elenco:
   l'inquadratura si rifa' quando cambia l'insieme (la sua "firma" e' la sequenza dei
   codici), e riordinare la tabella o il carrello non deve far saltare la mappa. */
export const trattePerMappa = (righe, geometrie) => righe
    .map((c) => {
        const g = geometrie ? geometrie[c.codice] : null;
        return {
            codice: c.codice,
            strada: c.via,
            tratto: c.tratto,
            lunghezzaMetri: c.metri,
            stato: c.livello,
            geometria: g ? g.geometria : null,
            geometriaQualita: g ? g.qualita : null,
        };
    })
    .sort((a, b) => String(a.codice).localeCompare(String(b.codice)));

const IconaPiuMeno = ({ meno, size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="5" y1="12" x2="19" y2="12" />
        {!meno && <line x1="12" y1="5" x2="12" y2="19" />}
    </svg>
);

const InclusaPill = () => (
    <span className="gp-rep-inclusa" title="Questa tratta è già nel percorso di recupero">
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
        </svg>
        nel recupero
    </span>
);

/**
 * La vista Mappa del tab.
 * @param righe          le righe dell'elenco GIA' filtrate (gli stessi filtri della tabella)
 * @param geo            risultato di useGeometrieRecuperi
 * @param codiciCarrello Set dei codici nel carrello (le linee blu)
 * @param inCarrello     Set degli id nel carrello (la chiave del carrello della pagina)
 * @param onPerno        aggiunge/toglie una riga dal carrello (perno della pagina)
 * @param onStorico      apre lo storico della tratta (modale Dettaglio della tabella)
 */
const VistaMappaRecuperi = ({ righe, geo, codiciCarrello, inCarrello, onPerno, onStorico, espansa = false }) => {
    const [selezionata, setSelezionata] = useState(null);            // codice della tratta scelta
    const [selezioneDaMappa, setSelezioneDaMappa] = useState(false);  // scelta col clic sulla mappa: niente zoom
    const latoRef = useRef(null);

    // Memo: MappaTratte ridisegna le linee quando cambia l'array, non a ogni render.
    const tratte = useMemo(() => trattePerMappa(righe, geo.geometrie), [righe, geo.geometrie]);
    const perCodice = useMemo(() => new Map(righe.map((c) => [c.codice, c])), [righe]);
    const conGeometria = useMemo(() => new Set(tratte.filter((t) => t.geometria).map((t) => t.codice)), [tratte]);
    const scelta = selezionata ? perCodice.get(selezionata) || null : null;

    // La tratta scelta esce dai filtri (o dall'elenco, dopo Aggiorna): la scelta si azzera.
    useEffect(() => {
        if (selezionata && !perCodice.has(selezionata)) setSelezionata(null);
    }, [selezionata, perCodice]);

    // Stessa logica della mappa del percorso: ritoccando la tratta scelta (toggle) la si lascia.
    const seleziona = useCallback((codice, { daMappa = false, toggle = false } = {}) => {
        setSelezioneDaMappa(daMappa);
        setSelezionata((prev) => (codice && prev === codice ? (toggle ? null : prev) : codice || null));
    }, []);

    // Sotto i 900px il pannello sta sotto la mappa: scegliendo una tratta lo si porta in vista.
    useEffect(() => {
        if (scelta && latoRef.current && window.innerWidth <= 900) {
            latoRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [scelta]);

    /* Etichetta al passaggio del mouse (solo dove c'e' l'hover): via, stella e se e' gia'
       nel recupero. HTML perche' la vuole Leaflet; i testi passano da esc(). */
    const tooltip = useCallback((t) => {
        const c = perCodice.get(t.codice);
        if (!c) return "";
        return `<div class="rp-tooltip-titolo">${esc(c.via)}</div>`
            + (c.tratto ? `<div>${esc(c.tratto)}</div>` : "")
            + `<div class="rp-tooltip-riga"><span class="rp-dot" style="background:${COLORE_STATO[c.livello]}"></span>${nomeStella(c)} · ${mancate(c)}</div>`
            + (inCarrello.has(c.id) ? `<div class="rp-tooltip-riga gp-mappa-tip-carrello">✓ Nel recupero</div>` : "");
    }, [perCodice, inCarrello]);

    const conteggi = useMemo(() => ({
        rosse: righe.filter((c) => c.livello === "rossa").length,
        gialle: righe.filter((c) => c.livello !== "rossa").length,
        nelRecupero: righe.filter((c) => inCarrello.has(c.id)).length,
        senzaGeometria: geo.geometrie ? righe.length - conGeometria.size : 0,
    }), [righe, inCarrello, geo.geometrie, conGeometria]);

    return (
        <div className="gp-mappa-rec">
            <div className="gp-mappa-legenda">
                <span className="gp-mappa-voce">
                    <i className="gp-mappa-dot" style={{ background: COLORE_STATO.rossa }} /> Stella rossa <strong>{nf(conteggi.rosse)}</strong>
                </span>
                <span className="gp-mappa-voce">
                    <i className="gp-mappa-dot" style={{ background: COLORE_STATO.gialla }} /> Stella gialla <strong>{nf(conteggi.gialle)}</strong>
                </span>
                <span className="gp-mappa-voce" title="Linea blu, col colore della stella come bordo">
                    <i className="gp-mappa-dot gp-mappa-dot-carrello" /> Nel recupero <strong>{nf(conteggi.nelRecupero)}</strong>
                </span>
                {conteggi.senzaGeometria > 0 && (
                    <span className="gp-mappa-nota">
                        {nf(conteggi.senzaGeometria)} tratt{conteggi.senzaGeometria === 1 ? "a" : "e"} senza geometria: in elenco ma non sulla mappa
                    </span>
                )}
            </div>

            {geo.loading ? (
                <div className="gp-gallery-loading"><Lf23Loader testo="Caricamento mappa..." /></div>
            ) : geo.errore && !geo.geometrie ? (
                <div className="gp-box-empty">
                    {geo.errore}
                    <div style={{ marginTop: 10 }}><button className="btn btn-ghost" onClick={geo.riprova}>Riprova</button></div>
                </div>
            ) : (
                <div className="gp-mappa-corpo">
                    <MappaTratte tratte={tratte} selezionata={selezionata} selezioneDaMappa={selezioneDaMappa}
                        onSeleziona={seleziona} popup={false} marcate={codiciCarrello} coloreScelta={COLORE_SCELTA}
                        tooltip={tooltip} espansa={espansa} className="gp-mappa-mappa" />

                    <div className="gp-mappa-lato" ref={latoRef}>
                        {scelta ? (
                            <DettaglioRecupero c={scelta} dentro={inCarrello.has(scelta.id)} sullaMappa={conGeometria.has(scelta.codice)}
                                onIndietro={() => seleziona(null)} onPerno={onPerno} onStorico={onStorico} />
                        ) : righe.length === 0 ? (
                            <div className="gp-box-empty">Nessuna tratta da recuperare con i filtri impostati.</div>
                        ) : (
                            righe.map((c) => {
                                const dentro = inCarrello.has(c.id);
                                return (
                                    <div key={c.id} className={`gp-rec-riga ${dentro ? "dentro" : ""}`}>
                                        <button type="button" className="gp-geo-riga" onClick={() => seleziona(c.codice)}>
                                            <div className="gp-geo-riga-testa">
                                                <span className="gp-mappa-riga-via">
                                                    <Stella livello={c.livello} volte={c.volte} />
                                                    <span className="gp-geo-via">{c.via || "(senza strada)"}</span>
                                                </span>
                                                {geo.geometrie && !conGeometria.has(c.codice) && (
                                                    <span className="gp-mappa-nogeo" title="Tratta senza geometria: non compare sulla mappa">no mappa</span>
                                                )}
                                            </div>
                                            <div className="gp-geo-riga-sub">
                                                {[c.tratto, c.metri != null ? `${nf(c.metri)} m` : null].filter(Boolean).join(" · ")}
                                                {dentro && <InclusaPill />}
                                            </div>
                                        </button>
                                        <button type="button" className={`icon-btn ${dentro ? "danger" : "primary"}`} onClick={() => onPerno(c)}
                                            title={dentro ? "Togli dal percorso di recupero" : "Aggiungi al percorso di recupero"}
                                            aria-label={dentro ? `Togli ${c.via} dal percorso di recupero` : `Aggiungi ${c.via} al percorso di recupero`}>
                                            <IconaPiuMeno meno={dentro} />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

/** Pannello della tratta scelta: i dati della riga di tabella, e i due comandi. */
const DettaglioRecupero = ({ c, dentro, sullaMappa, onIndietro, onPerno, onStorico }) => (
    <div className="gp-mappa-dettaglio">
        <button type="button" className="btn btn-ghost gp-new-btn gp-mappa-indietro" onClick={onIndietro}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
            Tutte le tratte
        </button>
        <div className="gp-mappa-dettaglio-testa">
            <div style={{ minWidth: 0 }}>
                <div className="gp-geo-via">{c.via || "(senza strada)"}</div>
                <div className="gp-geo-riga-sub">{[c.tratto, c.codice].filter(Boolean).join(" · ")}</div>
            </div>
            {dentro && <InclusaPill />}
        </div>
        <div className="gp-rec-stella">
            <Stella livello={c.livello} volte={c.volte} />
            <span><strong>{nomeStella(c)}</strong> · {mancate(c)}</span>
        </div>
        <div className="gp-detail-grid gp-mappa-info">
            <div className="gp-detail-full">
                <span className="gp-detail-label">Percorsi associati</span>
                {c.percorsi && c.percorsi.length ? (
                    <div className="gp-percorsi-cell">
                        {c.percorsi.map((g) => <span className="gp-percorso-pill" key={g}>{g}</span>)}
                    </div>
                ) : <span className="gp-detail-val">—</span>}
            </div>
            {c.quartiere && (
                <div>
                    <span className="gp-detail-label">Quartiere</span>
                    <span className="gp-detail-val">{quartiere(c.quartiere)}</span>
                </div>
            )}
            <div>
                <span className="gp-detail-label">Lunghezza</span>
                <span className="gp-detail-val">{c.metri != null ? `${nf(c.metri)} m` : "—"}</span>
            </div>
            <div>
                <span className="gp-detail-label">Passaggi/anno</span>
                <span className="gp-detail-val">{c.pas ?? "—"}</span>
            </div>
            <div>
                <span className="gp-detail-label">Ultimo passaggio saltato</span>
                <span className="gp-detail-val">{formatData(c.ultima)}</span>
            </div>
        </div>
        {!sullaMappa && <div className="gp-mappa-nota">Tratta non georeferenziata: non compare sulla mappa.</div>}
        <div className="gp-rec-azioni">
            <button type="button" className={`btn ${dentro ? "btn-ghost gp-rec-togli" : "btn-primary"}`} onClick={() => onPerno(c)}>
                <IconaPiuMeno meno={dentro} size={16} />
                {dentro ? "Togli dal recupero" : "Aggiungi al recupero"}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => onStorico(c)} title="Storico della tratta nei percorsi">
                Storico
            </button>
        </div>
    </div>
);

/**
 * La mappa del modale "Crea percorso di recupero": le tratte del carrello, ognuna col
 * numero della sua posizione nel giro. Riordinando l'elenco i numeri si spostano e la
 * mappa resta dov'e' (le tratte sono in ordine di codice, l'ordine sta solo nei numeri).
 * Toccare una linea (o il suo numero) dice a chi la usa quale riga far vedere.
 * @param voci     [{ c, i }]: le righe mostrate nell'elenco del modale, con la posizione REALE i
 * @param geo      risultato di useGeometrieRecuperi
 * @param onScegli codice della tratta toccata
 * @param filtro   nome del quartiere mostrato, se l'elenco e' filtrato (la mappa lo segue)
 */
export const MappaCarrello = ({ voci, geo, onScegli, filtro = null }) => {
    const [selezionata, setSelezionata] = useState(null);
    const righe = useMemo(() => voci.map((v) => v.c), [voci]);
    const tratte = useMemo(() => trattePerMappa(righe, geo.geometrie), [righe, geo.geometrie]);
    const etichette = useMemo(() => new Map(voci.map(({ c, i }) => [c.codice, String(i + 1)])), [voci]);
    const senzaGeometria = geo.geometrie ? tratte.filter((t) => !t.geometria).length : 0;

    // Una tratta tolta dal carrello (o nascosta dal filtro) non resta scelta.
    useEffect(() => {
        if (selezionata && !righe.some((c) => c.codice === selezionata)) setSelezionata(null);
    }, [selezionata, righe]);

    const onScegliRef = useRef(onScegli);
    useEffect(() => { onScegliRef.current = onScegli; }, [onScegli]);
    // Ritoccando la tratta scelta la si lascia; scegliendone una si avvisa chi usa la mappa.
    const seleziona = useCallback((codice, { toggle = false } = {}) => {
        setSelezionata((prev) => (codice && prev === codice && toggle ? null : codice || null));
    }, []);
    useEffect(() => { if (selezionata && onScegliRef.current) onScegliRef.current(selezionata); }, [selezionata]);

    return (
        <div className="gp-rec-mappa">
            {geo.loading ? (
                <div className="gp-gallery-loading"><Lf23Loader testo="Caricamento mappa..." /></div>
            ) : geo.errore && !geo.geometrie ? (
                <div className="gp-box-empty">
                    Mappa non disponibile: le tratte restano nell’elenco.
                    <div style={{ marginTop: 10 }}><button className="btn btn-ghost" onClick={geo.riprova}>Riprova</button></div>
                </div>
            ) : (
                <>
                    {/* selezioneDaMappa fisso: qui si sceglie solo toccando la mappa, che non deve saltare */}
                    <MappaTratte tratte={tratte} selezionata={selezionata} selezioneDaMappa onSeleziona={seleziona}
                        popup={false} etichette={etichette} coloreScelta={COLORE_SCELTA} className="gp-mappa-mappa" />
                    <div className="gp-rec-mappa-nota">
                        {filtro
                            ? <>Solo le tratte di {filtro}: il numero è la posizione nel giro completo. </>
                            : <>Il numero è la posizione nel giro. </>}
                        Tocca una linea per trovarla nell’elenco.
                        {senzaGeometria > 0 && (
                            <span className="gp-mappa-nota"> {nf(senzaGeometria)} tratt{senzaGeometria === 1 ? "a" : "e"} senza geometria non {senzaGeometria === 1 ? "è" : "sono"} sulla mappa.</span>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default VistaMappaRecuperi;
